import classnames from 'classnames';
import Title from './title.jsx';
import { saveLastPage } from '../actions/membership';
import { sendPageRead, sendNavigatedToPage } from '../actions/events';

import PropTypes from 'prop-types';

import React, { PureComponent } from 'react';
import TextViewer from './text_viewer.jsx';
import ReaderNavbar from './engage_reader/navbar.jsx';
import { ZingTouch } from 'unizin-lib';
import { analyticsEvent } from '../actions/analytics';
import { update, newMarkup, getHighlights } from '../actions/markup';
import { routeToContentPage } from '../actions/route';
import { setCurrentTerm } from '../actions/terms';
import { fetchSectionByUrl } from 'actions/section';
import { newNotification } from 'actions/notifications';
import { KEYS } from '../const';
import connect from './connect';
import { TAGNAMES } from 'const';
import { addAural } from 'actions/aural_notification';
import { setSkipLink, clearSkipLink } from 'actions/skip_link';
import Loader from './loader';

import mapStateToProps from 'selectors/component_selectors/engage_reader_selector';

class EngageReader extends PureComponent {
    static displayName = 'EngageReader';

    static propTypes = {
        rootStateHack: PropTypes.object,
        routeToContentPage: PropTypes.func.isRequired,
        children: PropTypes.object,
        newMarkup: PropTypes.func.isRequired,
        update: PropTypes.func.isRequired,
        section: PropTypes.object,
        content: PropTypes.object,
        contentId: PropTypes.number.isRequired,
        isInstructor: PropTypes.bool.isRequired,
        page: PropTypes.object,
        pageLabel: PropTypes.string.isRequired,
        pagenum: PropTypes.number.isRequired,
        scale: PropTypes.number.isRequired,
        scaleCenter: PropTypes.any,
        imgsrc: PropTypes.string.isRequired,
        lastHighlightColor: PropTypes.string.isRequired,
        highlights: PropTypes.array.isRequired,
        setCurrentTerm: PropTypes.func.isRequired,
        saveLastPage: PropTypes.func.isRequired,
        sendPageRead: PropTypes.func.isRequired,
        sendNavigatedToPage: PropTypes.func.isRequired,
        analyticsEvent: PropTypes.func.isRequired,
        chapters: PropTypes.array.isRequired,
        prevPage: PropTypes.shape({
            pagenum: PropTypes.number,
        }),
        nextPage: PropTypes.shape({
            pagenum: PropTypes.number,
        }),
        isConnected: PropTypes.bool.isRequired,
        getHighlights: PropTypes.func.isRequired,
        fetchSectionByUrl: PropTypes.func.isRequired,
        addAural: PropTypes.func.isRequired,
        setSkipLink: PropTypes.func.isRequired,
        clearSkipLink: PropTypes.func.isRequired,
        newNotification: PropTypes.func.isRequired,
        hideSharingQA: PropTypes.bool.isRequired,
    };

    state = {
        direction: 'next',
        hideSharingQA: false,
        loaded: true,
    };

    viewer = React.createRef();

    pageReadTimer = null;

    pageChangeTimeout = null;

    navRef = direction => el => {
        this[`nav-page-${direction}`] = el;
    };

    async componentDidMount() {
        const {
            addAural,
            content: { id, section, title },
            contentId,
            fetchSectionByUrl,
            getHighlights,
            pageLabel,
            pagenum,
            section: { id: sectionId },
            setSkipLink,
        } = this.props;
        setSkipLink({ to: `notes/content-${contentId}`, text: 'All Notes' });
        addAural(`${title}, Page ${pageLabel}`);
        getHighlights(contentId, pagenum);
        fetchSectionByUrl(section);
        document.addEventListener('keyup', this.onKeyUp);
        this.focusBody();
        if (id && sectionId) {
            this.saveLastPage(this.props);
        }
    }

    componentDidUpdate(oldProps) {
        const {
            pagenum,
            pageLabel,
            content,
            contentId,
            getHighlights,
            section,
            addAural,
        } = this.props;
        const { direction } = this.state;
        if (
            section.id &&
            content &&
            !(oldProps.section.id && oldProps.content)
        ) {
            this.props.setCurrentTerm(section.term_id);
            this.saveLastPage(this.props);
        }
        if (pagenum !== oldProps.pagenum) {
            this.saveLastPage(this.props);
            getHighlights(contentId, pagenum);
            this[`nav-page-${direction}`].focus();
        }
        if (pageLabel !== oldProps.pageLabel) {
            addAural(`Page ${pageLabel}`);
        }
    }

    componentWillUnmount() {
        if (this.pageReadTimer != null) {
            clearTimeout(this.pageReadTimer);
        }
        document.removeEventListener('keyup', this.onKeyUp);
        this.props.clearSkipLink();
    }

    focusBody() {
        // non-interactive elements need a tabIndex to be focusable
        document.body.setAttribute('tabIndex', 0);
        document.body.focus();
        document.body.removeAttribute('tabIndex');
    }

    onKeyUp = event => {
        const { scale } = this.props;

        // Arrow keys should not navigate when zoomed in.
        if (scale <= 1) {
            // Arrow keys should not navigate if you have anything focussed.
            // That would cause problems with writing notes.
            if (event.which === KEYS.LEFT && this._noFocus()) {
                this.prevPage();
            }
            if (event.which === KEYS.RIGHT && this._noFocus()) {
                this.nextPage();
            }
        }
    };

    onPressProxy = event => {
        this.viewer.current.onPress(event);
    };

    _noFocus() {
        const { activeElement } = document;
        if (activeElement) {
            const { tagName } = activeElement;
            return (
                tagName !== TAGNAMES.INPUT &&
                tagName !== TAGNAMES.TEXTAREA &&
                !activeElement.getAttribute('data-page-navigation')
            );
        }
    }

    onLoad = () => {
        setTimeout(
            () => {
                clearTimeout(this.pageChangeTimeout);
                this.pageChangeTimeout = null;
                this.setState({ loaded: true });
            },
            this.pageChangeTimeout ? 0 : 250
        );
    };

    onSwipe = ({
        detail: {
            data: [{ currentDirection }],
        },
    }) => {
        if (this.state.loaded) {
            if (currentDirection >= 345 || currentDirection <= 15) {
                this.prevPage();
            }
            if (currentDirection >= 165 && currentDirection <= 195) {
                this.nextPage();
            }
        }
    };

    changePage = direction => () => {
        const { content } = this.props;
        const page = this.props[`${direction}Page`];
        if (page && !this.pageChangeTimeout) {
            this.setState({ direction });
            this.props.analyticsEvent(`${direction}-page`, 'click');
            this.props.routeToContentPage({
                contentId: content.id,
                pagenum: page.pagenum,
            });
            this.pageChangeTimeout = setTimeout(() => {
                this.pageChangeTimeout = null;
                this.setState({ loaded: false });
            }, 200);
        }
    };

    nextPage = this.changePage('next');
    prevPage = this.changePage('prev');

    saveLastPage = props => {
        const { section, content, pagenum, pageLabel } = props;
        const { slug } = props.content;

        if (this.pageReadTimer) {
            clearTimeout(this.pageReadTimer);
        }
        this.pageReadTimer = setTimeout(() => {
            this.pageReadTimer = null;
            if (
                this.props.pagenum == pagenum &&
                this.props.pageLabel == pageLabel &&
                this.props.section.id == section.id &&
                this.props.content.slug == slug
            ) {
                this.props.sendPageRead(pagenum, pageLabel, slug, section);
            }
        }, 10000);

        this.props.saveLastPage(content, pagenum);
        this.props.sendNavigatedToPage(content.id, pagenum, pageLabel);
    };

    render() {
        const {
            chapters,
            children,
            content,
            contentId,
            hideSharingQA,
            highlights,
            imgsrc,
            isConnected,
            lastHighlightColor,
            newMarkup,
            page,
            pageLabel,
            pagenum,
            scale,
            scaleCenter,
            update,
        } = this.props;
        const { loaded } = this.state;
        const childComponent = children
            ? React.cloneElement(children, { content })
            : false;

        if (!page) {
            return null;
        }
        return (
            <ZingTouch
                onSwipe={this.onSwipe}
                capture={false}
                preventDefault={false}
            >
                <ReaderNavbar.Proxy
                    chapters={chapters}
                    pageLabel={pageLabel}
                    pagenum={pagenum}
                    content={content}
                    onPrevPage={this.prevPage}
                    onNextPage={this.nextPage}
                    hideSharingQA={hideSharingQA}
                />

                <Title>{`Page ${page.label}, ${content.title}`}</Title>

                {childComponent}

                <button
                    className={classnames(
                        'prev-page',
                        'icon',
                        'cl-icon',
                        'cl-icon-angle-left'
                    )}
                    ref={this.navRef('prev')}
                    onClick={this.prevPage}
                >
                    <span className="aural">previous page</span>
                </button>
                <button
                    className={classnames(
                        'next-page',
                        'icon',
                        'cl-icon',
                        'cl-icon-angle-right'
                    )}
                    ref={this.navRef('next')}
                    onClick={this.nextPage}
                >
                    <span className="aural">next page</span>
                </button>
                <TextViewer
                    canHighlight={isConnected}
                    content={content}
                    contentId={contentId}
                    hideSharingQA={hideSharingQA}
                    highlights={highlights}
                    htmlPopover
                    imgsrc={imgsrc}
                    keyboardNavigation
                    lastHighlightColor={lastHighlightColor}
                    newMarkup={newMarkup}
                    page={page}
                    pagenum={pagenum}
                    ref={this.viewer}
                    rootStateHack={this.props.rootStateHack}
                    scale={scale}
                    scaleCenter={scaleCenter}
                    update={update}
                    onLoad={this.onLoad}
                    newNotification={this.props.newNotification}
                />
                {loaded ? <div /> : <Loader />}
            </ZingTouch>
        );
    }
}

export default connect(
    mapStateToProps,
    {
        routeToContentPage,
        newMarkup,
        update,
        setCurrentTerm,
        saveLastPage,
        sendPageRead,
        sendNavigatedToPage,
        analyticsEvent,
        getHighlights,
        fetchSectionByUrl,
        addAural,
        setSkipLink,
        clearSkipLink,
        newNotification,
    }
)(EngageReader);



// WEBPACK FOOTER //
// ./src/components/engage_reader.jsx